/**
 * Shared builder for customer-facing payment breakdown lines.
 * Used by customer accept-bid UI and provider Bid Details so both sides see the same totals.
 */

import { PaymentBreakdownLine } from '../components/PaymentBreakdownTable';
import { DEFAULT_PLATFORM_FEES } from './feeCalculation';
import { ProviderSalesTaxRate, salesTaxOptionsForJob } from './salesTax';

export interface BidServicePricingForBreakdown {
  serviceName?: string;
  price?: number;
  frequency?: string;
  isSeasonalContract?: boolean;
  seasonalContractDetails?: {
    paymentSchedule?: string;
    totalContractPrice?: number;
    contractStartDate?: string | Date;
    contractEndDate?: string | Date;
    monthlyPaymentAmount?: number;
  };
}

export function buildCustomerPaymentBreakdownLines(options: {
  servicePricing?: BidServicePricingForBreakdown[] | null;
  /** Fallback when servicePricing is empty */
  fallbackPrice?: number | null;
  fallbackLabel?: string;
  platformFeePercent?: number | null;
  salesTaxRates?: ProviderSalesTaxRate[] | null;
  address?: { state?: string; zipCode?: string } | null;
  propertyType?: string | null;
}): PaymentBreakdownLine[] {
  const platformFeePercent =
    options.platformFeePercent != null && !Number.isNaN(options.platformFeePercent)
      ? options.platformFeePercent
      : DEFAULT_PLATFORM_FEES.standardFeePercentage;

  const taxOpts = salesTaxOptionsForJob(
    options.salesTaxRates,
    options.address,
    options.propertyType
  );

  const pricing = Array.isArray(options.servicePricing) ? options.servicePricing : [];

  if (pricing.length === 0) {
    const price = Number(options.fallbackPrice) || 0;
    if (price <= 0) return [];
    return [
      {
        label: options.fallbackLabel || 'Service',
        servicePrice: price,
        platformFeePercent,
        chargeNote: 'per visit',
        ...taxOpts,
      },
    ];
  }

  return pricing.map((sp) => {
    const isSeasonalContract = sp.isSeasonalContract || sp.frequency === 'seasonal-contract';
    const seasonalDetails = sp.seasonalContractDetails;
    const paymentSchedule = seasonalDetails?.paymentSchedule || 'single-payment';
    const label = sp.serviceName || 'Service';

    if (isSeasonalContract && paymentSchedule === 'monthly' && seasonalDetails?.totalContractPrice) {
      const start = new Date(seasonalDetails.contractStartDate as any);
      const end = new Date(seasonalDetails.contractEndDate as any);
      const startsOnFirst = start.getUTCDate() === 1;
      let billingMonths = startsOnFirst
        ? (end.getUTCFullYear() - start.getUTCFullYear()) * 12 + (end.getUTCMonth() - start.getUTCMonth()) + 1
        : (() => {
            const ny = start.getUTCMonth() === 11 ? start.getUTCFullYear() + 1 : start.getUTCFullYear();
            const nm = (start.getUTCMonth() + 1) % 12;
            return (end.getUTCFullYear() - ny) * 12 + (end.getUTCMonth() - nm) + 1;
          })();
      const monthlyAmount =
        seasonalDetails.monthlyPaymentAmount != null &&
        !Number.isNaN(Number(seasonalDetails.monthlyPaymentAmount))
          ? Number(seasonalDetails.monthlyPaymentAmount)
          : billingMonths > 0
            ? Math.round((seasonalDetails.totalContractPrice / billingMonths) * 100) / 100
            : seasonalDetails.totalContractPrice;
      return {
        label,
        servicePrice: monthlyAmount,
        platformFeePercent,
        chargeNote: 'per month',
        ...taxOpts,
      };
    }

    const servicePrice =
      isSeasonalContract && seasonalDetails?.totalContractPrice
        ? seasonalDetails.totalContractPrice
        : Number(sp.price) || 0;

    return {
      label,
      servicePrice,
      platformFeePercent,
      chargeNote: isSeasonalContract ? 'at first service' : 'per visit',
      ...taxOpts,
    };
  });
}
